import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomerLayout from '../../components/layouts/CustomerLayout';
import StatusBadge from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Filter, ArrowRight, History as HistoryIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const History = () => {
  const navigate = useNavigate();
  const [waybills, setWaybills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    const fetchWaybills = async () => {
      try {
        let url = `${API_URL}/api/waybills?page=${page}&limit=${limit}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (statusFilter !== 'all') url += `&status=${statusFilter}`;

        const response = await axios.get(url);
        setWaybills(response.data.waybills);
        setTotal(response.data.total);
      } catch (err) {
        console.error('Error fetching waybills:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchWaybills();
  }, [page, search, statusFilter]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `R ${Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  const totalPages = Math.ceil(total / limit);

  const statuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'quote_requested', label: 'Quote Requested' },
    { value: 'in_costing', label: 'In Costing' },
    { value: 'quote_ready', label: 'Quote Ready' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'declined', label: 'Declined' },
  ];

  return (
    <CustomerLayout>
      <div className="animate-fade-in" data-testid="history-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Shipment History</h1>
            <p className="text-slate-600 mt-1">View all your past and current shipments</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="card-elevated mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search by waybill number or company..."
                  className="pl-10"
                  data-testid="history-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-48" data-testid="history-status-filter">
                  <Filter className="h-4 w-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="card-elevated">
          <CardHeader className="border-b border-slate-100 pb-4">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <HistoryIcon className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <CardTitle className="card-title">All Waybills</CardTitle>
                <p className="text-sm text-slate-500">{total} total records</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
              </div>
            ) : waybills.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Waybill ID</th>
                        <th>Submission Date</th>
                        <th>Origin</th>
                        <th>Destination</th>
                        <th>Quote Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {waybills.map((wb) => (
                        <tr 
                          key={wb.waybill_id}
                          className="cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => navigate(`/waybill/${wb.waybill_id}`, { state: { from: '/history' } })}
                        >
                          <td>
                            <span className="text-teal-700 font-medium">{wb.reference_number}</span>
                          </td>
                          <td>{formatDate(wb.submission_date)}</td>
                          <td>{wb.origin_city}</td>
                          <td>{wb.destination_city}</td>
                          <td className="font-medium">{formatCurrency(wb.quoted_amount)}</td>
                          <td><StatusBadge status={wb.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p - 1)}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                        const pageNum = page <= 2 ? i + 1 : page - 1 + i;
                        if (pageNum > totalPages) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                            className={page === pageNum ? 'bg-teal-700' : ''}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-slate-500">
                No waybills found
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </CustomerLayout>
  );
};

export default History;
